import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { parseScheduleDateLocal } from "../utils/dateUtils";

function ConfirmDeleteDialog(props) {
  const { open, onClose, onConfirm, schedule, isLoading, title, message } = props;

  const handleConfirm = () => {
    onConfirm(schedule);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      keepMounted
      aria-labelledby="confirm-delete-dialog-title"
      aria-describedby="confirm-delete-dialog-description"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="confirm-delete-dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          <Typography variant="h6" component="span">
            {title || 'Confirm Delete Schedule'}
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <DialogContentText id="confirm-delete-dialog-description" sx={{ mb: 2 }}>
          {message || 'Are you sure you want to delete the disconnect schedule for:'}
        </DialogContentText>
        
        <Box sx={{ 
          p: 2, 
          backgroundColor: 'grey.50', 
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'grey.300'
        }}>
          {schedule?.holdType ? (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Hold Type: {schedule.holdType}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Target: {
                  schedule.holdType === 'GROUP' 
                    ? schedule.groupName || 'Unknown Group'
                    : schedule.holdType === 'PROPERTY'
                    ? schedule.propertyName || 'Unknown Property'
                    : schedule.holdType === 'ACCOUNT'
                    ? schedule.accountNum || 'Unknown Account'
                    : 'Unknown Target'
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Reason: {schedule.reason || 'No reason provided'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Expires: {schedule.expiresAt ? 
                  new Date(schedule.expiresAt).toLocaleDateString() : 'N/A'}
              </Typography>
              {schedule.liftReason && (
                <Typography variant="body2" color="text.secondary">
                  Lift Reason: {schedule.liftReason}
                </Typography>
              )}
            </>
          ) : (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Group: {schedule?.group?.name || schedule?.groupName || 'Unknown Group'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Scheduled Date: {(() => {
                  const d = parseScheduleDateLocal(schedule?.scheduledDisconnectDate);
                  return d ? format(d, 'MM/dd/yyyy') : 'N/A';
                })()}
              </Typography>
              {schedule?.notes && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Notes: {schedule.notes}
                </Typography>
              )}
            </>
          )}
        </Box>
        
        <DialogContentText sx={{ mt: 2, color: 'error.main', fontWeight: 600 }}>
          ⚠️ This action cannot be undone.
        </DialogContentText>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button 
          onClick={onClose} 
          variant="outlined"
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isLoading}
          startIcon={<DeleteIcon />}
        >
          {isLoading ? 'Deleting...' : (schedule?.holdType ? 'Delete Hold' : 'Delete Schedule')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ConfirmDeleteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  schedule: PropTypes.object,
  isLoading: PropTypes.bool,
  title: PropTypes.string,
  message: PropTypes.string,
};

export default ConfirmDeleteDialog;
